import { captureException } from '../services/sentryService';
import { Nullable } from '../types';

export const hasText = (input: any) => {
  return input && typeof input === 'string' && (input as string).length;
};

export const removeDecimal = (input: number) => {
  return input && parseInt(input.toFixed(2).toString().replace('.', ''));
};

export const safeStringify = (value: any) => {
  // just in case the value is an object with circular references
  try {
    return JSON.stringify(value);
  } catch (error) {
    captureException({
      error,
    });
    return '';
  }
};

export function objectHasKey(object: object, key: string): key is keyof object {
  return key in object;
}

export function messageOfUnknownError(error: unknown): string {
  const key = 'message';
  if (typeof error === 'undefined') {
    return 'undefined'; // we handle this explicitly because JSON.stringify does not output a string when passed undefined
  } else if (typeof error === 'string') {
    return error; // we already have a string
  } else if (typeof error === 'object' && error !== null && objectHasKey(error, key)) {
    const value = error[key]; // the message field is there, it should be a string but a test cannot hurt
    return typeof value === 'string' ? value : safeStringify(value);
  } else {
    return safeStringify(error);
  }
}

export function defaultString(value: unknown, defaultValue = ''): string {
  return typeof value === 'string' ? value : defaultValue;
}

export function defaultBoolean(value: unknown, defaultValue = false): boolean {
  return typeof value === 'boolean' ? value : defaultValue;
}

export function defaultNullString(value: unknown): Nullable<string> {
  return typeof value === 'string' ? value : null;
}

// require an explicit name (instead of using fn.name) in order to accept anonymous functions, for instance the ones created by useCallback
export function exposeGlobally(name: string, fn: (...args: unknown[]) => unknown): () => void {
  (window as any).Payment = {
    ...((window as any).Payment || {}),
    [name]: fn,
  };
  return () => {
    (window as any).Payment[name] = undefined;
  };
}

export function catchFatalError<Inputs extends unknown[]>(
  onFatalError: (error: unknown) => void,
  fn: (...args: Inputs) => void,
) {
  return (...args: Inputs) => {
    try {
      fn(...args);
    } catch (error: unknown) {
      onFatalError(error);
    }
  };
}
